import { useCallback } from 'react';
import type { ParamListBase } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';

import { useSafeFocusEffect, useSafePathname } from '@sg/garnish';

import { useIsExpoRouterEnabled } from '@order/shared/hooks';

import { redirectsMapping } from '../../../../navigation/AppNavigation.redirects';
import { useSafeExpoNavigationReset } from '../../../../routing';

// ─────────────────────────────────────────────────────────────────────────────

// Here we handle 301 redirects using the redirects mapping.
export const usePermanentRedirects = (params: PermanentRedirectsParams) => {
  const { navigation, route } = params;

  const isExpoRouterEnabled = useIsExpoRouterEnabled();
  const pathname = useSafePathname();
  const resetNavigation = useSafeExpoNavigationReset();

  const path = route?.path ?? pathname;
  const redirectTo = path ? redirectsMapping[path] : null;
  const expoRedirectTo = path ? EXPO_ROUTER_REDIRECTS[path] : null;

  useSafeFocusEffect(
    useCallback(() => {
      if (isExpoRouterEnabled && expoRedirectTo) {
        resetNavigation?.(getExpoResetState(expoRedirectTo));

        return;
      }

      if (redirectTo && navigation) {
        navigation.replace(redirectTo.screen, redirectTo.params);
      }
    }, [isExpoRouterEnabled, expoRedirectTo, resetNavigation, redirectTo, navigation]),
  );

  return Boolean(isExpoRouterEnabled ? expoRedirectTo : redirectTo);
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function getExpoResetState(mapping: TabRouteMapping): ExpoResetState {
  const { tabName, screenName } = mapping;

  if (!screenName) {
    return {
      index: 0,
      routes: [{ name: '(tabs)' as any, state: { routes: [{ name: tabName as any }] } }],
    };
  }

  return {
    index: 0,
    routes: [
      {
        name: '(tabs)' as any,
        state: {
          routes: [{ name: tabName as any, state: { routes: [{ name: screenName as any }] } }],
        },
      },
    ],
  };
}

// ─── Constants ───────────────────────────────────────────────────────────────

const EXPO_ROUTER_REDIRECTS: Record<string, TabRouteMapping> = {
  '/location': { tabName: '(menu)', screenName: 'locations/index' },
  '/menu': { tabName: 'account', screenName: 'menu/index' },
  '/profile': { tabName: 'account', screenName: 'info/index' },
  '/account/profile': { tabName: 'account', screenName: 'info/index' },
  '/challenges': { tabName: 'sg-rewards' },
  '/payment': { tabName: 'account', screenName: 'payment-and-gift-cards/index' },
  '/addresses': { tabName: 'account', screenName: 'addresses/index' },
  '/orders': { tabName: 'account', screenName: 'orders/index' },
  '/favorites': { tabName: 'account', screenName: 'favorites/index' },
  '/account-dietary-restrictions': { tabName: 'account', screenName: 'dietary-restrictions/index' },
};

// ─── Types ───────────────────────────────────────────────────────────────────

type PermanentRedirectsParams = {
  navigation: NativeStackNavigationProp<ParamListBase> | null;
  route: { path?: string } | null;
};

type TabRouteMapping = {
  tabName: string;
  screenName?: string;
};

type ExpoResetState = Parameters<NonNullable<ReturnType<typeof useSafeExpoNavigationReset>>>[0];
